import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { eventService, EventParticipant, EventConfig } from '../services/eventService';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../components/Toast';
import './EventPage.css';

const EventPage: React.FC = () => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const { showToast } = useToast();

    // State
    const [config, setConfig] = useState<EventConfig | null>(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [hasParticipated, setHasParticipated] = useState(false);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;
    const [myParticipation, setMyParticipation] = useState<EventParticipant | null>(null);
    const [participants, setParticipants] = useState<EventParticipant[]>([]);
    const [totalParticipants, setTotalParticipants] = useState(0);

    // Form state
    const [zaloName, setZaloName] = useState('');
    const [phone, setPhone] = useState('');
    const [isReturningCustomer, setIsReturningCustomer] = useState(false);
    const [chosenNumbers, setChosenNumbers] = useState<string[]>(['']);
    const [numberStatuses, setNumberStatuses] = useState<{ [key: string]: 'checking' | 'taken' | 'available' | '' }>({});

    // Screenshot state
    const [fbScreenshot, setFbScreenshot] = useState<File | null>(null);
    const [fbPreview, setFbPreview] = useState<string | null>(null);
    const [ytScreenshot, setYtScreenshot] = useState<File | null>(null);
    const [ytPreview, setYtPreview] = useState<string | null>(null);

    // Audio state
    const [isMuted, setIsMuted] = useState(false);
    const audioRef = useRef<HTMLAudioElement | null>(null);

    const toggleMute = () => {
        if (audioRef.current) {
            audioRef.current.muted = !isMuted;
            setIsMuted(!isMuted);
            if (!isMuted === false) {
                audioRef.current.play().catch(() => { });
            }
        }
    };

    useEffect(() => {
        if (!loading && config?.is_active && audioRef.current) {
            audioRef.current.volume = 0.5;
            const playPromise = audioRef.current.play();
            if (playPromise !== undefined) {
                playPromise.catch(error => {
                    console.log("Autoplay prevented:", error);
                    setIsMuted(true);
                });
            }
        }
    }, [loading, config]);

    useEffect(() => {
        loadData();
    }, [user]);

    const loadData = async () => {
        try {
            setLoading(true);
            const configRes = await eventService.getConfig();
            setConfig(configRes.config);

            const participantsRes = await eventService.getPublicParticipants({ per_page: 100 });
            setParticipants(participantsRes.participants);
            setTotalParticipants(participantsRes.total);

            if (user) {
                try {
                    const myRes = await eventService.getMyParticipation();
                    setHasParticipated(myRes.has_participated);
                    if (myRes.participant) {
                        setMyParticipation(myRes.participant);
                    }
                } catch (e) {
                    // Not logged in or error
                }
            }
        } catch (error: any) {
            console.error('Error loading event data:', error);
            showToast(t('event.errors.loadEvent'), 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleCustomerTypeChange = (isReturning: boolean) => {
        setIsReturningCustomer(isReturning);
        const maxNumbers = isReturning ? 2 : 1;
        if (chosenNumbers.length > maxNumbers) {
            setChosenNumbers(chosenNumbers.slice(0, maxNumbers));
        }
    };

    const checkNumber = useCallback(async (number: string, index: number) => {
        if (number.length !== 4) {
            setNumberStatuses(prev => ({ ...prev, [index]: '' }));
            return;
        }
        setNumberStatuses(prev => ({ ...prev, [index]: 'checking' }));
        try {
            const result = await eventService.checkNumber(number);
            setNumberStatuses(prev => ({
                ...prev,
                [index]: result.is_taken ? 'taken' : 'available'
            }));
        } catch (error) {
            setNumberStatuses(prev => ({ ...prev, [index]: '' }));
        }
    }, []);

    const handleNumberChange = (index: number, value: string) => {
        const cleaned = value.replace(/\D/g, '').slice(0, 4);
        const newNumbers = [...chosenNumbers];
        newNumbers[index] = cleaned;
        setChosenNumbers(newNumbers);
        if (cleaned.length === 4) {
            checkNumber(cleaned, index);
        } else {
            setNumberStatuses(prev => ({ ...prev, [index]: '' }));
        }
    };

    const addNumberInput = () => {
        const maxNumbers = isReturningCustomer ? 2 : 1;
        if (chosenNumbers.length < maxNumbers) {
            setChosenNumbers([...chosenNumbers, '']);
        }
    };

    const removeNumberInput = (index: number) => {
        if (chosenNumbers.length > 1) {
            const newNumbers = chosenNumbers.filter((_, i) => i !== index);
            setChosenNumbers(newNumbers);
        }
    };

    const handleFbScreenshotChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setFbScreenshot(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setFbPreview(reader.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleYtScreenshotChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setYtScreenshot(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setYtPreview(reader.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!zaloName.trim()) {
            showToast(t('event.validation.enterZalo'), 'error');
            return;
        }
        if (!phone.trim()) {
            showToast(t('event.validation.enterPhone'), 'error');
            return;
        }

        const validNumbers = chosenNumbers.filter(n => n.length === 4);
        if (validNumbers.length === 0) {
            showToast(t('event.validation.enterNumber'), 'error');
            return;
        }

        const hasTakenNumber = Object.values(numberStatuses).some(s => s === 'taken');
        if (hasTakenNumber) {
            showToast(t('event.validation.numberTaken'), 'error');
            return;
        }

        if (config?.fb_post_link && !fbScreenshot) {
            showToast(t('event.validation.fbScreenshot'), 'error');
            return;
        }
        if (config?.youtube_video_link && !ytScreenshot) {
            showToast(t('event.validation.ytScreenshot'), 'error');
            return;
        }

        setSubmitting(true);

        try {
            const result = await eventService.register({
                zalo_name: zaloName.trim(),
                phone: phone.trim(),
                is_returning_customer: isReturningCustomer,
                chosen_numbers: validNumbers
            });

            const participantId = result.participant.id;

            if (fbScreenshot && participantId) {
                await eventService.uploadScreenshot(participantId, 'fb', fbScreenshot);
            }
            if (ytScreenshot && participantId) {
                await eventService.uploadScreenshot(participantId, 'youtube', ytScreenshot);
            }

            showToast(t('event.toasts.success'), 'success');
            setHasParticipated(true);
            setMyParticipation(result.participant);
            loadData();
        } catch (error: any) {
            const errorMsg = error.response?.data?.error || t('event.errors.generic');
            showToast(errorMsg, 'error');
        } finally {
            setSubmitting(false);
        }
    };

    const getPrizeDisplay = (prizeType: string | null | undefined) => {
        switch (prizeType) {
            case 'first': return { label: t('event.prizes.labels.first'), className: 'first' };
            case 'second': return { label: t('event.prizes.labels.second'), className: 'second' };
            case 'third': return { label: t('event.prizes.labels.third'), className: 'third' };
            case 'encouragement': return { label: t('event.prizes.labels.encouragement'), className: 'encouragement' };
            default: return null;
        }
    };

    if (loading) {
        return (
            <div className="event-page">
                <div className="loading-spinner">
                    <div className="spinner"></div>
                </div>
            </div>
        );
    }

    if (!config?.is_active) {
        return (
            <div className="event-page">
                <div className="event-container">
                    <div className="event-header">
                        <h1 className="event-title">{t('event.notStarted.title')}</h1>
                        <p className="event-subtitle">{t('event.notStarted.subtitle')}</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="event-page">
            <audio ref={audioRef} src="/images/introxuan.mp3" loop autoPlay />

            <button
                className="music-control-btn"
                onClick={toggleMute}
                title={isMuted ? t('event.music.turnOn') : t('event.music.turnOff')}
            >
                {isMuted ? '🔇' : '🔊'}
            </button>

            <div className="side-banner left-banner">
                <img src="/images/chucmungnammoi.jpg" alt="Happy New Year" />
            </div>
            <div className="side-banner right-banner">
                <img src="/images/chucmungnammoi.jpg" alt="Happy New Year" />
            </div>

            <div className="event-container">
                <div className="event-header">
                    <h1 className="event-title">{config?.event_name || t('event.header.defaultTitle')}</h1>
                    <p className="event-subtitle">{t('event.header.subtitle')}</p>
                </div>

                {/* Prizes Section */}
                <div className="prizes-section">
                    <h2 className="prizes-title">{t('event.prizes.title')}</h2>
                    <div className="prizes-grid">
                        <div className="prize-card first">
                            <div className="prize-rank">{t('event.prizes.first.rank')}</div>
                            <div className="prize-name">{t('event.prizes.first.name')}</div>
                            <div className="prize-description">{t('event.prizes.first.description')}</div>
                        </div>
                        <div className="prize-card second">
                            <div className="prize-rank">{t('event.prizes.second.rank')}</div>
                            <div className="prize-name">{t('event.prizes.second.name')}</div>
                            <div className="prize-description">{t('event.prizes.second.description')}</div>
                        </div>
                        <div className="prize-card third">
                            <div className="prize-rank">{t('event.prizes.third.rank')}</div>
                            <div className="prize-name">{t('event.prizes.third.name')}</div>
                            <div className="prize-description">{t('event.prizes.third.description')}</div>
                        </div>
                    </div>
                    <p className="prizes-note">{t('event.prizes.note')}</p>
                </div>

                {/* Rules Section */}
                <div className="rules-section">
                    <h2 className="section-title">{t('event.rules.title')}</h2>
                    <ul className="rules-list">
                        <li dangerouslySetInnerHTML={{ __html: t('event.rules.returning') }} />
                        <li dangerouslySetInnerHTML={{ __html: t('event.rules.new') }} />
                        <li dangerouslySetInnerHTML={{ __html: t('event.rules.format') }} />
                        <li dangerouslySetInnerHTML={{ __html: t('event.rules.duplicate') }} />
                        <li dangerouslySetInnerHTML={{ __html: t('event.rules.required') }} />
                        <li>{t('event.rules.verification')}</li>
                    </ul>
                </div>

                {/* Registration Form or Success */}
                {!user ? (
                    <div className="login-required">
                        <h3 className="login-title">{t('event.login.title')}</h3>
                        <p className="login-text">{t('event.login.text')}</p>
                        <a href="/login" className="login-btn">{t('event.login.button')}</a>
                    </div>
                ) : hasParticipated ? (
                    <div className="success-message">
                        <div className="success-icon">{t('event.success.icon')}</div>
                        <h3 className="success-title">{t('event.success.title')}</h3>
                        <p className="success-text">{t('event.success.text')}</p>
                        {myParticipation && (
                            <div style={{ marginTop: '1rem', color: '#888' }}>
                                <p>{t('event.success.chosenNumbers')}: <strong style={{ color: '#ffd700' }}>{myParticipation.chosen_numbers?.join(', ')}</strong></p>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="registration-section">
                        <h2 className="section-title">{t('event.registration.title')}</h2>

                        {(config?.fb_post_link || config?.youtube_video_link) && (
                            <div className="social-requirements">
                                <p className="social-intro">{t('event.registration.socialIntro')}</p>

                                <div className="social-grid">
                                    {config?.fb_post_link && (
                                        <div className="social-card">
                                            <h4 className="social-title">{t('event.registration.step1.title')}</h4>
                                            <a href={config.fb_post_link} target="_blank" rel="noopener noreferrer" className="social-link">
                                                {t('event.registration.step1.button')}
                                            </a>
                                            <p className="social-instruction">{t('event.registration.step1.instruction')}</p>
                                            <div className="upload-area" onClick={() => document.getElementById('fb-screenshot')?.click()}>
                                                {fbPreview ? (
                                                    <div className="preview-container">
                                                        <img src={fbPreview} alt="FB Screenshot" className="preview-image" />
                                                        <button type="button" className="remove-btn" onClick={(e) => { e.stopPropagation(); setFbScreenshot(null); setFbPreview(null); }}>×</button>
                                                    </div>
                                                ) : (
                                                    <>
                                                        <div className="upload-icon">+</div>
                                                        <div className="upload-text">{t('event.registration.uploadFb')}</div>
                                                    </>
                                                )}
                                            </div>
                                            <input id="fb-screenshot" type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFbScreenshotChange} />
                                        </div>
                                    )}

                                    {config?.youtube_video_link && (
                                        <div className="social-card">
                                            <h4 className="social-title">{t('event.registration.step2.title')}</h4>
                                            <a href={config.youtube_video_link} target="_blank" rel="noopener noreferrer" className="social-link">
                                                {t('event.registration.step2.button')}
                                            </a>
                                            <p className="social-instruction">{t('event.registration.step2.instruction')}</p>
                                            <div className="upload-area" onClick={() => document.getElementById('yt-screenshot')?.click()}>
                                                {ytPreview ? (
                                                    <div className="preview-container">
                                                        <img src={ytPreview} alt="YT Screenshot" className="preview-image" />
                                                        <button type="button" className="remove-btn" onClick={(e) => { e.stopPropagation(); setYtScreenshot(null); setYtPreview(null); }}>×</button>
                                                    </div>
                                                ) : (
                                                    <>
                                                        <div className="upload-icon">+</div>
                                                        <div className="upload-text">{t('event.registration.uploadYt')}</div>
                                                    </>
                                                )}
                                            </div>
                                            <input id="yt-screenshot" type="file" accept="image/*" style={{ display: 'none' }} onChange={handleYtScreenshotChange} />
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        <form className="registration-form" onSubmit={handleSubmit}>
                            <div className="customer-type-toggle">
                                <label className={`customer-type-option ${!isReturningCustomer ? 'active' : ''}`} onClick={() => handleCustomerTypeChange(false)}>
                                    <input type="radio" name="customerType" checked={!isReturningCustomer} onChange={() => { }} />
                                    <span className="customer-type-label">{t('event.registration.customerType.new')}</span>
                                    <span className="customer-type-desc">{t('event.registration.customerType.newDesc')}</span>
                                </label>
                                <label className={`customer-type-option ${isReturningCustomer ? 'active' : ''}`} onClick={() => handleCustomerTypeChange(true)}>
                                    <input type="radio" name="customerType" checked={isReturningCustomer} onChange={() => { }} />
                                    <span className="customer-type-label">{t('event.registration.customerType.returning')}</span>
                                    <span className="customer-type-desc">{t('event.registration.customerType.returningDesc')}</span>
                                </label>
                            </div>

                            <div className="form-group">
                                <label className="form-label">{t('event.registration.form.zaloName')}</label>
                                <input type="text" className="form-input" placeholder={t('event.registration.form.zaloPlaceholder')} value={zaloName} onChange={(e) => setZaloName(e.target.value)} required />
                            </div>

                            <div className="form-group">
                                <label className="form-label">{t('event.registration.form.phone')}</label>
                                <input type="tel" className="form-input" placeholder={t('event.registration.form.phonePlaceholder')} value={phone} onChange={(e) => setPhone(e.target.value.replace(/\D/g, ''))} required />
                            </div>

                            <div className="numbers-section">
                                <label className="form-label">
                                    {t('event.registration.form.luckyNumbers', { count: isReturningCustomer ? 2 : 1 })}
                                </label>
                                <div className="numbers-inputs">
                                    {chosenNumbers.map((num, index) => (
                                        <div key={index} className="number-input-wrapper">
                                            <input type="text" className="number-input" placeholder="0000" value={num} onChange={(e) => handleNumberChange(index, e.target.value)} maxLength={4} />
                                            {numberStatuses[index] === 'checking' && (<span className="number-status">...</span>)}
                                            {numberStatuses[index] === 'taken' && (<span className="number-status taken">{t('event.registration.form.numberTaken')}</span>)}
                                            {numberStatuses[index] === 'available' && (<span className="number-status available">{t('event.registration.form.numberAvailable')}</span>)}
                                            {chosenNumbers.length > 1 && (<button type="button" onClick={() => removeNumberInput(index)} className="remove-number-btn">×</button>)}
                                        </div>
                                    ))}
                                    {chosenNumbers.length < (isReturningCustomer ? 2 : 1) && (
                                        <button type="button" onClick={addNumberInput} className="add-number-btn">{t('event.registration.form.addNumber')}</button>
                                    )}
                                </div>
                            </div>

                            <div className="warning-notice">
                                <span className="warning-icon">!</span>
                                <span className="warning-text">
                                    <strong>{t('event.registration.warning.title')}</strong> {t('event.registration.warning.text')}
                                </span>
                            </div>

                            <button type="submit" className="submit-btn" disabled={submitting}>
                                {submitting ? t('event.registration.submitting') : t('event.registration.submit')}
                            </button>
                        </form>
                    </div>
                )}

                {/* Participants Table */}
                <div className="participants-section">
                    <h2 className="section-title">{t('event.participants.title')} ({totalParticipants})</h2>
                    {participants.length === 0 ? (
                        <p style={{ textAlign: 'center', color: '#a8a8b3' }}>{t('event.participants.empty')}</p>
                    ) : (
                        <table className="participants-table">
                            <thead>
                                <tr>
                                    <th>{t('event.participants.table.number')}</th>
                                    <th>{t('event.participants.table.name')}</th>
                                    <th>{t('event.participants.table.time')}</th>
                                    <th>{t('event.participants.table.prize')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {participants.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map((p, index) => {
                                    const prize = getPrizeDisplay(p.prize_type);
                                    const actualIndex = (currentPage - 1) * itemsPerPage + index + 1;
                                    return (
                                        <tr key={p.id}>
                                            <td>{actualIndex}</td>
                                            <td>{p.zalo_name}</td>
                                            <td>{new Date(p.submitted_at).toLocaleString()}</td>
                                            <td>{prize ? (<span className={`prize-badge ${prize.className}`}>{prize.label}</span>) : '-'}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}

                    {participants.length > itemsPerPage && (
                        <div className="pagination-controls">
                            <button className="pagination-btn" disabled={currentPage === 1} onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}>
                                {t('event.participants.pagination.prev')}
                            </button>
                            <span className="pagination-info">
                                {t('event.participants.pagination.page')} <strong style={{ color: '#FFD700' }}>{currentPage}</strong> / {Math.ceil(participants.length / itemsPerPage)}
                            </span>
                            <button className="pagination-btn" disabled={currentPage >= Math.ceil(participants.length / itemsPerPage)} onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.ceil(participants.length / itemsPerPage)))}>
                                {t('event.participants.pagination.next')}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default EventPage;
